import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CTLNotification from "application/components/CTLNotification";
import { application } from "globals/endpoints";
import { Attributes, operationURLS, summaryColumns, detailColumns } from "./model";

function downloadBlob(data, filename) {
  const blob = new Blob([data], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
}

function FeedBackReport({ onLoad, registerCallBacks, pageId, searchText, pageName }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(20);
  const [detailCell, setDetailCell] = useState(null); // { feedbackDate, rating }

  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "", pagename: "", status: "" });

  useEffect(() => {
    Attributes && onLoad && onLoad(Attributes);
    registerCallBacks && registerCallBacks({});
  }, []);

  const canSearch = useMemo(() => Boolean(fromDate) && Boolean(toDate) && fromDate <= toDate, [fromDate, toDate]);

  function runSearch() {
    if (!canSearch) return;
    setLoading(true);
    setSearched(true);
    application
      .post(operationURLS.summary, { fromDate, toDate })
      .then((res) => setSummaryRows(res?.data?.data ?? []))
      .catch(() => {
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to load feedback summary" });
        setSummaryRows([]);
      })
      .finally(() => setLoading(false));
  }

  function openDetail(feedbackDate, rating, nextPage = 0, nextSize = detailPageSize) {
    setDetailCell({ feedbackDate, rating });
    setDetailOpen(true);
    setDetailLoading(true);
    application
      .post(operationURLS.detail, { fromDate, toDate, feedbackDate, rating, page: nextPage, pageSize: nextSize })
      .then((res) => {
        setDetailRows(res?.data?.data?.rows ?? []);
        setDetailTotal(res?.data?.data?.total ?? 0);
        setDetailPage(nextPage);
        setDetailPageSize(nextSize);
      })
      .catch(() => {
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to load feedback detail" });
        setDetailRows([]);
        setDetailTotal(0);
      })
      .finally(() => setDetailLoading(false));
  }

  function onChangeDetailPage(_e, newPage) {
    openDetail(detailCell.feedbackDate, detailCell.rating, newPage, detailPageSize);
  }

  function onChangeDetailRowsPerPage(e) {
    openDetail(detailCell.feedbackDate, detailCell.rating, 0, parseInt(e.target.value, 10));
  }

  function downloadCsv(url, filename) {
    application
      .post(url, { fromDate, toDate }, { responseType: "arraybuffer" })
      .then((response) => {
        downloadBlob(response.data, filename);
        setNotify({ isOpen: true, pagename: pageName, type: "success", status: "CSV Download successful" });
      })
      .catch(() =>
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to download CSV" })
      );
  }

  return (
    <MDBox p={2}>
      <Paper elevation={0} variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { colorScheme: darkMode ? "dark" : "light" } }}
            sx={{ flex: { md: 1 } }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { colorScheme: darkMode ? "dark" : "light" } }}
            sx={{ flex: { md: 1 } }}
          />
          <MDButton
            variant="outlined"
            color="dark"
            startIcon={<Icon>search</Icon>}
            disabled={!canSearch}
            onClick={runSearch}
            sx={{ width: 110, flexShrink: 0 }}
          >
            Search
          </MDButton>
          <Tooltip title="Download Summary CSV">
            <span style={{ visibility: searched && !loading && summaryRows.length > 0 ? "visible" : "hidden" }}>
              <IconButton color="inherit" onClick={() => downloadCsv(operationURLS.summaryCsv, "feedback-summary.csv")}>
                <Icon>summarize</Icon>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download Detail CSV">
            <span style={{ visibility: searched && !loading && summaryRows.length > 0 ? "visible" : "hidden" }}>
              <IconButton color="inherit" onClick={() => downloadCsv(operationURLS.detailCsv, "feedback-detail.csv")}>
                <Icon>download</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {!searched && (
        <Typography variant="body2" color="text">
          Choose a From Date and To Date, then click Search to view the day-wise feedback summary.
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {searched && !loading && summaryRows.length === 0 && (
        <Typography variant="body2" color="text">
          {`No feedback found between ${fromDate} and ${toDate}.`}
        </Typography>
      )}

      {searched && !loading && summaryRows.length > 0 && (
        <TableContainer sx={{ width: "100%", maxWidth: "100%", overflow: "auto" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              "& .MuiTableCell-stickyHeader": {
                backgroundColor: (theme) => theme.palette.background.card || theme.palette.white.main,
              },
            }}
          >
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                {summaryColumns.map((c) => (
                  <TableCell key={c.header}>{c.header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryRows.map((row) => (
                <TableRow key={row.feedbackDate} hover>
                  {summaryColumns.map((c) => {
                    const value = row[c.accessor] ?? 0;
                    if (!c.rating) {
                      return <TableCell key={c.header}>{value}</TableCell>;
                    }
                    return (
                      <TableCell key={c.header}>
                        {value > 0 ? (
                          <Link component="button" underline="hover" onClick={() => openDetail(row.feedbackDate, c.rating)}>
                            {value}
                          </Link>
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {detailCell ? `Feedback Detail — ${detailCell.feedbackDate}, Rating ${detailCell.rating}` : "Feedback Detail"}
            </Typography>
            <IconButton onClick={() => setDetailOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {!detailLoading && detailRows.length === 0 && (
            <Typography variant="body2" color="text">No detail rows found.</Typography>
          )}
          {!detailLoading && detailRows.length > 0 && (
            <>
              <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    "& .MuiTableCell-stickyHeader": {
                      backgroundColor: (theme) => theme.palette.background.card || theme.palette.white.main,
                    },
                  }}
                >
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      {detailColumns.map((c) => (
                        <TableCell key={c.header}>{c.header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailRows.map((row, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={`${row.callTime}-${idx}`} hover>
                        {detailColumns.map((c) => (
                          <TableCell key={c.header}>{row[c.accessor]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={detailTotal}
                page={detailPage}
                rowsPerPage={detailPageSize}
                onPageChange={onChangeDetailPage}
                onRowsPerPageChange={onChangeDetailRowsPerPage}
                rowsPerPageOptions={[20, 50, 100]}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}

FeedBackReport.propTypes = {
  onLoad: PropTypes.func,
  registerCallBacks: PropTypes.func,
  pageId: PropTypes.string,
  searchText: PropTypes.string,
  pageName: PropTypes.string,
};

FeedBackReport.defaultProps = {
  onLoad: null,
  registerCallBacks: null,
  pageId: "",
  searchText: "",
  pageName: "Feedback Report",
};

export default FeedBackReport;
